import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { useEffect, useSyncExternalStore } from "react";
import { q } from "./api.ts";
import { lastProduct, rememberProduct, subscribeProduct } from "./current-product.ts";
import { productOfPath } from "./sidebar-items.ts";

// The product a page shows (docs/design-system.md §0.2, plan M7 step 7c): the
// one in the address (`/p/:product/…`) decides. Pages that are not a
// product's own (the playbooks, settings) may still take `?product=`, else the
// product last opened, else the first. Whichever a product page shows is
// remembered, so `/` and the jumps return to it.

/** `validateSearch` for a page that can be narrowed to one product. */
export const productSearch = (search: Record<string, unknown>): { product?: string } => (typeof search.product === "string" && search.product ? { product: search.product } : {});

/** The first candidate that is one of the workspace's products, then the last one used, then the first. */
export function currentProduct(products: C.Product[], ...candidates: (string | null | undefined)[]): C.Product | undefined {
  for (const id of [...candidates, lastProduct()]) {
    const found = id ? products.find((product) => product.id === id) : undefined;
    if (found) return found;
  }
  return products[0];
}

/** The workspace's products and the one shown here; `implied` is a product the page's object belongs to. */
export function useCurrentProduct(implied?: string | null) {
  const products = useQuery(q.products);
  const location = useRouterState({ select: (state) => state.location });
  const remembered = useSyncExternalStore(subscribeProduct, lastProduct, () => null);
  const inPath = productOfPath(location.pathname);
  const search = location.search as { product?: unknown };
  const requested = typeof search.product === "string" ? search.product : null;
  const product = currentProduct(products.data ?? [], inPath, requested, implied);
  const id = product?.id;
  useEffect(() => { if (inPath && id === inPath && id !== remembered) rememberProduct(id); }, [id, inPath, remembered]);
  return { products, product };
}
